import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { app } from '../firebaseConfig';

class FCMService {
    constructor() {
        this.messaging = null;
        this.currentToken = null;
        
        try {
            // Initialize messaging
            this.messaging = getMessaging(app);
            console.log('FCMService: Firebase Messaging initialized');
        } catch (error) {
            console.error('FCMService: Error initializing messaging:', error);
        }
    }

    /**
     * Check if FCM is supported in this browser
     */
    isSupported() {
        return 'serviceWorker' in navigator && 
               'PushManager' in window &&
               'Notification' in window;
    }

    /**
     * Request permission and get FCM token
     * @returns {string|null} The FCM token or null if permission denied
     */
    async getToken() {
        console.log('FCMService: Starting getToken process...');
        
        if (!this.isSupported()) {
            console.log('FCMService: FCM is not supported in this browser');
            return null;
        }

        if (!this.messaging) {
            console.error('FCMService: Messaging not initialized');
            return null;
        }

        try {
            // Check current permission state
            const currentPermission = Notification.permission;
            console.log('FCMService: Current notification permission:', currentPermission);
            
            if (currentPermission !== 'granted') {
                console.log('FCMService: Permission not granted, returning null');
                return null;
            }

            // Check VAPID key
            const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY;
            console.log('FCMService: Using VAPID key:', vapidKey?.substring(0, 20) + '...');
            
            if (!vapidKey || vapidKey === 'REPLACE_WITH_YOUR_ACTUAL_VAPID_KEY') {
                console.error('FCMService: Invalid VAPID key');
                return null;
            }

            // Get token - let Firebase handle service worker internally
            console.log('FCMService: Requesting FCM token from Firebase...');
            const currentToken = await getToken(this.messaging, {
                vapidKey: vapidKey
            });

            if (currentToken) {
                console.log('FCMService: FCM token obtained successfully');
                console.log('FCMService: Token:', currentToken.substring(0, 40) + '...');
                this.currentToken = currentToken;
                return currentToken;
            } else {
                console.log('FCMService: No FCM token available');
                return null;
            }
        } catch (error) {
            console.error('FCMService: Error getting FCM token:', error);
            console.error('FCMService: Error details:', {
                message: error.message,
                code: error.code,
                stack: error.stack
            });
            return null;
        }
    }

    /**
     * Explicitly request notification permission
     * @returns {string} The permission result
     */
    async requestNotificationPermission() {
        if (!('Notification' in window)) {
            console.error('FCMService: Notifications not supported');
            return 'unsupported';
        }
        
        try {
            console.log('FCMService: Explicitly requesting notification permission...');
            const permission = await Notification.requestPermission();
            console.log('FCMService: Permission result:', permission);
            return permission;
        } catch (error) {
            console.error('FCMService: Error requesting permission:', error);
            return 'error';
        }
    }

    /**
     * Handle foreground messages
     * @param {Function} callback - Function to call when message is received
     */
    onMessage(callback) {
        if (!this.messaging) {
            console.error('FCMService: Messaging not initialized');
            return;
        }

        return onMessage(this.messaging, (payload) => {
            console.log('FCMService: Message received in foreground:', payload);
            callback(payload);
        });
    }

    /**
     * Get the current token
     * @returns {string|null} The current FCM token
     */
    getCurrentToken() {
        return this.currentToken;
    }
}

// Export a singleton instance
const fcmService = new FCMService();
export default fcmService;